import httpCommon from "./http-common";
import InvoiceService from "./invoices.service";
import ChartOfAccountsService from "./chartofaccounts.service";
import TransactionsService from "./transactions.service";
import BillsService from "./bills.service";
import ReceiptService from "./receipts.service";

// Financial reports data
const getIncomeStatementData = async (businessId: number, startDate: Date, endDate: Date) => {
  // Convert dates to year, month, day format for API calls
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1; // Months are zero-based
  const startDay = startDate.getDate();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;
  const endDay = endDate.getDate();

  try {
    // Get all required data in parallel
    const [incomeTransactions, expenseTransactions, invoicesData, receiptsData, billsData] = await Promise.all([
      // Get income transactions
      TransactionsService.getTransactionsByDateRange(
        businessId, startYear, startMonth, startDay, endYear, endMonth, endDay
      ),
      // Get expense transactions
      TransactionsService.getTransactionsByDateRange(
        businessId, startYear, startMonth, startDay, endYear, endMonth, endDay
      ),
      // Get invoices in date range
      httpCommon.get(`/v1/getDataUsingDateRange/invoices/${businessId}/${startYear}/${startMonth}/${startDay}/${endYear}/${endMonth}/${endDay}`),
      // Get receipts in date range
      httpCommon.get(`/v1/getDataUsingDateRange/receipts/${businessId}/${startYear}/${startMonth}/${startDay}/${endYear}/${endMonth}/${endDay}`),
      // Get bills in date range
      httpCommon.get(`/v1/getDataUsingDateRange/bills/${businessId}/${startYear}/${startMonth}/${startDay}/${endYear}/${endMonth}/${endDay}`)
    ]);

    // Calculate total revenue
    const totalInvoiceAmount = invoicesData.data.invoices.reduce((sum: number, invoice: any) => sum + Number(invoice.amount), 0);
    const totalReceiptAmount = receiptsData.data.receipts.reduce((sum: number, receipt: any) => sum + Number(receipt.amount), 0);
    console.log("totalReceiptAmount", totalReceiptAmount);
    console.log("totalInvoiceAmount", totalInvoiceAmount);
    // Calculate total expenses
    console.log("billsData.data", billsData.data);
    const totalBillAmount = billsData.data.bills.reduce((sum: number, bill: any) => sum + Number(bill.amount), 0);

    console.log("incomeTransactions.data", incomeTransactions.data);
    // Filter income transactions (credit entries to income accounts)
    const incomeAccounts = incomeTransactions.data.accountTransactions.filter((tx: any) => 
      tx.transactionType === 'CREDIT' && 
      (tx.type === 'INCOME' || tx.type === 'REVENUE')
    );
    console.log("incomeAccounts", incomeAccounts);

    console.log("expenseTransactions.data", expenseTransactions.data);
    // Filter expense transactions (debit entries to expense accounts)
    const expenseAccounts = expenseTransactions.data.accountTransactions.filter((tx: any) => 
      tx.transactionType === 'DEBIT' && 
      tx.type === 'EXPENSE'
    );

    // Group income by category
    const incomeByCategory = incomeAccounts.reduce((acc: any, tx: any) => {
      const category = tx.accountName || 'Other Income';
      if (!acc[category]) {
        acc[category] = 0;
      }
      acc[category] += Number(tx.amount);
      return acc;
    }, {});

    // Group expenses by category
    const expensesByCategory = expenseAccounts.reduce((acc: any, tx: any) => {
      const category = tx.accountName || 'Other Expenses';
      if (!acc[category]) {
        acc[category] = 0;
      }
      acc[category] += Number(tx.amount);
      return acc;
    }, {});

    // Calculate net income
    const totalIncome = (Object.values(incomeByCategory) as number[]).reduce((sum: number, amount: number) => sum + amount, 0);
    const totalExpenses = (Object.values(expensesByCategory) as number[]).reduce((sum: number, amount: number) => sum + amount, 0);
    const netIncome = totalIncome - totalExpenses;

    return {
      periodStart: startDate,
      periodEnd: endDate,
      revenue: {
        total: totalInvoiceAmount,
        categories: incomeByCategory,
      },
      expenses: {
        total: totalBillAmount,
        categories: expensesByCategory,
      },
      totalIncome,
      totalExpenses,
      netIncome,
      data: {
        invoices: invoicesData.data.invoices,
        receipts: receiptsData.data.receipts,
        bills: billsData.data.bills,
        incomeTransactions: incomeAccounts,
        expenseTransactions: expenseAccounts
      }
    };
  } catch (error) {
    console.error("Error fetching income statement data:", error);
    throw error;
  }
};

const getBalanceSheetData = async (businessId: number, asOfDate: Date) => {
  try {
    // Get chart of accounts
    const chartOfAccountsResponse = await ChartOfAccountsService.getAllBusinessChartOfAccounts(businessId);
    const chartOfAccounts = chartOfAccountsResponse.data.businessChartOfAccounts || [];

    console.log("getBalanceSheetData chartOfAccounts", chartOfAccounts);
    // Filter accounts by type
    const assetAccounts = chartOfAccounts.filter((account: any) => account.type === 'ASSET');
    console.log("getBalanceSheetData assetAccounts", assetAccounts);
    const liabilityAccounts = chartOfAccounts.filter((account: any) => account.type === 'LIABILITY');
    console.log("getBalanceSheetData liabilityAccounts", liabilityAccounts);
    const equityAccounts = chartOfAccounts.filter((account: any) => account.type === 'EQUITY');
    console.log("getBalanceSheetData equityAccounts", equityAccounts);

    // Get account balances
    const accountBalancePromises = chartOfAccounts.map(async (account: any) => {
        console.log("getBalanceSheetData account", account);
      // We would need an API endpoint to get account balance as of a date
      // For now, let's assume we have one or use transactions to calculate
      try {
        const year = asOfDate.getFullYear();
        const month = asOfDate.getMonth() + 1;
        const day = asOfDate.getDate();
        
        const transactionsResponse = await TransactionsService.getTransactionsByDateRange(
          businessId, 2000, 1, 1, year, month, day, account.publicId
        );
        
        // Calculate balance based on transactions
        let balance = 0;
        console.log("getBalanceSheetData transactionsResponse.data", transactionsResponse.data);
        transactionsResponse.data.accountTransactions.forEach((tx: any) => {
        console.log("getBalanceSheetData tx.transactionType", tx.transactionType);
        console.log("getBalanceSheetData tx.amount", tx.amount);
        console.log("getBalanceSheetData account.type", account.type);
          if (tx.transactionType === 'DEBIT') {
            if (['ASSET', 'EXPENSE'].includes(account.type)) {
              balance += Number(tx.amount);
            } else {
              balance -= Number(tx.amount);
            }
          } else { // CREDIT
            if (['LIABILITY', 'EQUITY', 'INCOME'].includes(account.type)) {
              balance += Number(tx.amount);
            } else {
              balance -= Number(tx.amount);
            }
          }
        });
        
        return {
          ...account,
          balance
        };
      } catch (error) {
        console.error(`Error getting balance for account ${account.name}:`, error);
        return {
          ...account,
          balance: 0
        };
      }
    });
    
    const accountsWithBalances = await Promise.all(accountBalancePromises);
    console.log("getBalanceSheetData accountsWithBalances", accountsWithBalances);
    // Calculate totals
    const totalAssets = accountsWithBalances
      .filter((account: any) => account.type === 'ASSET')
      .reduce((sum: number, account: any) => sum + account.balance, 0);
    console.log("getBalanceSheetData totalAssets", totalAssets);
    const totalLiabilities = accountsWithBalances
      .filter((account: any) => account.type === 'LIABILITY')
      .reduce((sum: number, account: any) => sum + account.balance, 0);
    console.log("getBalanceSheetData totalLiabilities", totalLiabilities);
    const totalEquity = accountsWithBalances
      .filter((account: any) => account.type === 'EQUITY')
      .reduce((sum: number, account: any) => sum + account.balance, 0);
    console.log("getBalanceSheetData totalEquity", totalEquity);

    return {
      asOfDate,
      assets: {
        accounts: accountsWithBalances.filter((account: any) => account.type === 'ASSET'),
        total: totalAssets
      },
      liabilities: {
        accounts: accountsWithBalances.filter((account: any) => account.type === 'LIABILITY'),
        total: totalLiabilities
      },
      equity: {
        accounts: accountsWithBalances.filter((account: any) => account.type === 'EQUITY'),
        total: totalEquity
      },
      totalLiabilitiesAndEquity: totalLiabilities + totalEquity
    };
  } catch (error) {
    console.error("Error fetching balance sheet data:", error);
    throw error;
  }
};

const getCashFlowData = async (businessId: number, startDate: Date, endDate: Date) => {
  // Convert dates to year, month, day format for API calls
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1;
  const startDay = startDate.getDate();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;
  const endDay = endDate.getDate();

  try {
    // Get all required data in parallel
    const [transactions, receiptsData, billsData] = await Promise.all([
      // Get all transactions in date range
      TransactionsService.getTransactionsByDateRange(
        businessId, startYear, startMonth, startDay, endYear, endMonth, endDay
      ),
      // Get receipts in date range (cash inflows)
      httpCommon.get(`/v1/getDataUsingDateRange/receipts/${businessId}/${startYear}/${startMonth}/${startDay}/${endYear}/${endMonth}/${endDay}`),
      // Get bills in date range (cash outflows)
      httpCommon.get(`/v1/getDataUsingDateRange/bills/${businessId}/${startYear}/${startMonth}/${startDay}/${endYear}/${endMonth}/${endDay}`)
    ]);

    // Get cash accounts
    const chartOfAccountsResponse = await ChartOfAccountsService.getAllBusinessChartOfAccounts(businessId);
    const cashAccounts = chartOfAccountsResponse.data.chartOfAccounts.filter(
      (account: any) => account.type === 'ASSET' && account.accountSubtype === 'CASH'
    );

    // Filter cash-related transactions
    const cashTransactions = transactions.data.transactions.filter((tx: any) => 
      cashAccounts.some((account: any) => account.id === tx.accountId)
    );

    // Categorize cash flows
    const operatingActivities = cashTransactions.filter((tx: any) => 
      tx.referenceType === 'RECEIPT' || tx.referenceType === 'BILL' || 
      tx.referenceType === 'INVOICE' || tx.referenceType === 'EXPENSE'
    );

    const investingActivities = cashTransactions.filter((tx: any) => 
      tx.referenceType === 'ASSET_PURCHASE' || tx.referenceType === 'ASSET_SALE'
    );

    const financingActivities = cashTransactions.filter((tx: any) => 
      tx.referenceType === 'LOAN' || tx.referenceType === 'EQUITY' || 
      tx.referenceType === 'DIVIDEND' || tx.referenceType === 'OWNER_CONTRIBUTION'
    );

    // Calculate totals
    const calculateNetFlow = (txs: any[]) => {
      return txs.reduce((net: number, tx: any) => {
        return net + (tx.transactionType === 'CREDIT' ? tx.amount : -tx.amount);
      }, 0);
    };

    const operatingCashFlow = calculateNetFlow(operatingActivities);
    const investingCashFlow = calculateNetFlow(investingActivities);
    const financingCashFlow = calculateNetFlow(financingActivities);
    const netCashFlow = operatingCashFlow + investingCashFlow + financingCashFlow;

    return {
      periodStart: startDate,
      periodEnd: endDate,
      operatingActivities: {
        transactions: operatingActivities,
        netFlow: operatingCashFlow
      },
      investingActivities: {
        transactions: investingActivities,
        netFlow: investingCashFlow
      },
      financingActivities: {
        transactions: financingActivities,
        netFlow: financingCashFlow
      },
      netCashFlow,
      receipts: receiptsData.data.receipts,
      payments: billsData.data.bills,
      cashAccounts
    };
  } catch (error) {
    console.error("Error fetching cash flow data:", error);
    throw error;
  }
};

// Property reports data
const getRentalIncomeData = async (businessId: number, propertyIds: string[], startDate: Date, endDate: Date) => {
  // Convert dates to year, month, day format for API calls
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1;
  const startDay = startDate.getDate();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;
  const endDay = endDate.getDate();

  try {
    // For each property, fetch related invoices in date range
    const propertyDataPromises = propertyIds.map(async (propertyId) => {
      try {
        // We would need an API to get property details and invoices by property
        // For now, let's assume we make custom calls
        const invoicesResponse = await httpCommon.get(
          `/v1/getDataUsingDateRange/propertyInvoices/${businessId}/${propertyId}/${startYear}/${startMonth}/${startDay}/${endYear}/${endMonth}/${endDay}`
        );
        
        return {
          propertyId,
          invoices: invoicesResponse.data.invoices || []
        };
      } catch (error) {
        console.error(`Error fetching data for property ${propertyId}:`, error);
        return {
          propertyId,
          invoices: []
        };
      }
    });

    const propertiesData = await Promise.all(propertyDataPromises);
    
    // Calculate summary data
    const summaryData = propertiesData.map((propertyData) => {
      const totalInvoiced = propertyData.invoices.reduce(
        (sum: number, invoice: any) => sum + Number(invoice.amount), 0
      );
      
      const paidInvoices = propertyData.invoices.filter(
        (invoice: any) => invoice.status === 'PAID'
      );
      
      const totalPaid = paidInvoices.reduce(
        (sum: number, invoice: any) => sum + Number(invoice.amount), 0
      );
      
      const occupancyRate = 100; // This would need to be calculated based on property occupancy data
      
      return {
        propertyId: propertyData.propertyId,
        totalInvoiced,
        totalPaid,
        outstanding: totalInvoiced - totalPaid,
        occupancyRate,
        invoiceCount: propertyData.invoices.length
      };
    });
    
    return {
      periodStart: startDate,
      periodEnd: endDate,
      properties: propertiesData,
      summary: summaryData,
      totalInvoiced: summaryData.reduce((sum, property) => sum + property.totalInvoiced, 0),
      totalPaid: summaryData.reduce((sum, property) => sum + property.totalPaid, 0),
      totalOutstanding: summaryData.reduce((sum, property) => sum + property.outstanding, 0)
    };
  } catch (error) {
    console.error("Error fetching rental income data:", error);
    throw error;
  }
};

// Tenant reports data
const getTenantLedgerData = async (businessId: number, tenantIds: string[], startDate: Date, endDate: Date) => {
  // Convert dates to year, month, day format for API calls
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1;
  const startDay = startDate.getDate();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;
  const endDay = endDate.getDate();

  try {
    // For each tenant, fetch related invoices and receipts in date range
    const tenantDataPromises = tenantIds.map(async (tenantId) => {
      try {
        // We would need APIs to get tenant invoices and receipts
        const [invoicesResponse, receiptsResponse] = await Promise.all([
          httpCommon.get(
            `/v1/getDataUsingDateRange/tenantInvoices/${businessId}/${tenantId}/${startYear}/${startMonth}/${startDay}/${endYear}/${endMonth}/${endDay}`
          ),
          httpCommon.get(
            `/v1/getDataUsingDateRange/tenantReceipts/${businessId}/${tenantId}/${startYear}/${startMonth}/${startDay}/${endYear}/${endMonth}/${endDay}`
          )
        ]);
        
        return {
          tenantId,
          invoices: invoicesResponse.data.invoices || [],
          receipts: receiptsResponse.data.receipts || []
        };
      } catch (error) {
        console.error(`Error fetching data for tenant ${tenantId}:`, error);
        return {
          tenantId,
          invoices: [],
          receipts: []
        };
      }
    });

    const tenantsData = await Promise.all(tenantDataPromises);
    
    // Calculate summary data
    const summaryData = tenantsData.map((tenantData) => {
      const totalInvoiced = tenantData.invoices.reduce(
        (sum: number, invoice: any) => sum + Number(invoice.amount), 0
      );
      
      const totalPaid = tenantData.receipts.reduce(
        (sum: number, receipt: any) => sum + Number(receipt.amount), 0
      );
      
      return {
        tenantId: tenantData.tenantId,
        totalInvoiced,
        totalPaid,
        balance: totalInvoiced - totalPaid,
        invoiceCount: tenantData.invoices.length,
        receiptCount: tenantData.receipts.length
      };
    });
    
    return {
      periodStart: startDate,
      periodEnd: endDate,
      tenants: tenantsData,
      summary: summaryData,
      totalInvoiced: summaryData.reduce((sum, tenant) => sum + tenant.totalInvoiced, 0),
      totalPaid: summaryData.reduce((sum, tenant) => sum + tenant.totalPaid, 0),
      totalBalance: summaryData.reduce((sum, tenant) => sum + tenant.balance, 0)
    };
  } catch (error) {
    console.error("Error fetching tenant ledger data:", error);
    throw error;
  }
};

// Loan reports data
const getAmortizationScheduleData = (loanAmount: number, interestRate: number, loanTerm: number, paymentFrequency: string) => {
  // Calculate payment frequency in months
  let paymentsPerYear;
  switch (paymentFrequency) {
    case 'weekly':
      paymentsPerYear = 52;
      break;
    case 'bi-weekly':
      paymentsPerYear = 26;
      break;
    case 'monthly':
    default:
      paymentsPerYear = 12;
      break;
  }

  const totalPayments = (loanTerm / 12) * paymentsPerYear;
  const periodicInterestRate = (interestRate / 100) / paymentsPerYear;
  
  // Calculate monthly payment using amortization formula
  const payment = loanAmount * (
    periodicInterestRate * Math.pow(1 + periodicInterestRate, totalPayments)
  ) / (
    Math.pow(1 + periodicInterestRate, totalPayments) - 1
  );

  // Generate amortization schedule
  let remainingBalance = loanAmount;
  const schedule = [];

  for (let i = 1; i <= totalPayments; i++) {
    const interestPayment = remainingBalance * periodicInterestRate;
    const principalPayment = payment - interestPayment;
    remainingBalance -= principalPayment;

    schedule.push({
      paymentNumber: i,
      payment: payment,
      principalPayment: principalPayment,
      interestPayment: interestPayment,
      remainingBalance: Math.max(0, remainingBalance)
    });
  }

  return {
    loanAmount,
    interestRate,
    loanTerm,
    paymentFrequency,
    payment,
    totalPayments,
    totalInterest: schedule.reduce((sum, payment) => sum + payment.interestPayment, 0),
    schedule
  };
};

// Maintenance reports data
const getMaintenanceReportData = async (businessId: number, propertyIds: string[], categoryIds: string[], startDate: Date, endDate: Date) => {
  // Convert dates to year, month, day format for API calls
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1;
  const startDay = startDate.getDate();
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;
  const endDay = endDate.getDate();

  try {
    // We would need an API to get maintenance expenses by property and category
    // For now, let's assume a custom endpoint
    const maintenanceDataResponse = await httpCommon.get(
      `/v1/getDataUsingDateRange/maintenanceExpenses/${businessId}/${startYear}/${startMonth}/${startDay}/${endYear}/${endMonth}/${endDay}`,
      {
        params: {
          propertyIds: propertyIds.join(','),
          categoryIds: categoryIds.join(',')
        }
      }
    );
    
    const maintenanceExpenses = maintenanceDataResponse.data.expenses || [];
    
    // Group by property
    const expensesByProperty = maintenanceExpenses.reduce((acc: any, expense: any) => {
      if (!acc[expense.propertyId]) {
        acc[expense.propertyId] = [];
      }
      acc[expense.propertyId].push(expense);
      return acc;
    }, {});
    
    // Group by category
    const expensesByCategory = maintenanceExpenses.reduce((acc: any, expense: any) => {
      if (!acc[expense.categoryId]) {
        acc[expense.categoryId] = [];
      }
      acc[expense.categoryId].push(expense);
      return acc;
    }, {});
    
    // Calculate summaries
    const propertySummary = Object.keys(expensesByProperty).map((propertyId) => {
      const expenses = expensesByProperty[propertyId];
      const total = expenses.reduce((sum: number, expense: any) => sum + Number(expense.amount), 0);
      
      return {
        propertyId,
        expenseCount: expenses.length,
        totalAmount: total
      };
    });
    
    const categorySummary = Object.keys(expensesByCategory).map((categoryId) => {
      const expenses = expensesByCategory[categoryId];
      const total = expenses.reduce((sum: number, expense: any) => sum + Number(expense.amount), 0);
      
      return {
        categoryId,
        expenseCount: expenses.length,
        totalAmount: total
      };
    });
    
    return {
      periodStart: startDate,
      periodEnd: endDate,
      expenses: maintenanceExpenses,
      byProperty: expensesByProperty,
      byCategory: expensesByCategory,
      propertySummary,
      categorySummary,
      totalExpenses: maintenanceExpenses.reduce((sum: number, expense: any) => sum + Number(expense.amount), 0)
    };
  } catch (error) {
    console.error("Error fetching maintenance report data:", error);
    throw error;
  }
};

const ReportDataService = {
  // Financial reports
  getIncomeStatementData,
  getBalanceSheetData,
  getCashFlowData,
  
  // Property reports
  getRentalIncomeData,
  
  // Tenant reports
  getTenantLedgerData,
  
  // Loan reports
  getAmortizationScheduleData,
  
  // Maintenance reports
  getMaintenanceReportData
};

export default ReportDataService; 